import React from 'react';
import {
    PanelHeader,
    Div,
    SplitCol,
    SplitLayout,
    Button,
    ButtonGroup,
    FormItem,
    FormLayout,
    Input,
    Textarea,
    Switch,
    SimpleCell,
    Radio,
    RadioGroup,
    NativeSelect,
    Title,
    IconButton,
    Chip,
    DateInput,
} from '@vkontakte/vkui';
import {Icon16Clear, Icon20ListPlusOutline} from '@vkontakte/icons';
import IMask from 'imask';

import {
    AdaptivityContext,
    getMonitoringQuestions,
    getStaffPositions,
    getTtStaffPositions,
    getGUID,
    setQuizStep,
} from '../assets/constants';
import BaseComponent from '../components/BaseComponent';
import withOffline from '../components/withOffline';
import * as constants from '../assets/constants';
import KBInput from '../assets/KBInput';
import SalePointMonitoringDocsSync from '../utils/localDB/salePointMonitoringDocs';
import HelpButton from '../assets/HelpButton';

class SalePointMonitoringStep4Questionnaire extends BaseComponent {

    constructor(props) {
        super(props);

        const schema = getMonitoringQuestions();
        const {current_document} = props;
        const existing = (current_document && current_document.questionnaire)
            ? current_document.questionnaire
            : {answers: [], sellers: []};

        const answersMap = this.buildAnswersMap(existing.answers || []);
        let sellers = Array.isArray(existing.sellers)
            ? existing.sellers.filter(s => !s.removed).map(s => ({...s, position_id: s.position_id || 0}))
            : [];

        // Prefill sellers from sale_point if questionnaire has none yet
        if (sellers.length === 0) {
            const spSellers = current_document
                && current_document.doc_data
                && current_document.doc_data.sale_point
                && current_document.doc_data.sale_point.sellers;
            if (Array.isArray(spSellers) && spSellers.length > 0) {
                sellers = spSellers.map(s => ({
                    guid: s.guid || getGUID(),
                    last_name: s.last_name || '',
                    first_name: s.first_name || '',
                    middle_name: s.middle_name || '',
                    birth_date: s.birth_date || null,
                    phone: s.phone || '',
                    position_id: s.position_id || 0,
                    is_owner: s.is_owner ? 1 : 0,
                    is_lpr: s.is_lpr ? 1 : 0,
                    qualification: s.qualification || 0,
                    comment: s.comment || '',
                }));
            }
        }

        this.state = {
            questions: schema.questions || [],
            tree: schema.tree || [],
            qualifications: schema.qualifications || {},
            answersMap,
            sellers,
            errors: {},
            ...this.state,
        };

        this.saveTimeout = null;
        this.phoneMasks = {};
    }

    buildAnswersMap = (answers) => {
        const map = {};
        (answers || []).forEach(a => {
            if (!a || !a.question_id) return;
            const qid = a.question_id;
            if (!map[qid]) {
                map[qid] = {
                    question_id: qid,
                    question_title: a.question_title || '',
                    option_id: a.option_id ?? null,
                    option_ids: a.option_ids ? [...a.option_ids] : [],
                    value: a.value ?? '',
                    value_num: a.value_num ?? null,
                    values: a.values ? [...a.values] : [],
                };
            } else {
                // Merge multi-row answers (multiselect / tree from server)
                if (a.option_id != null) {
                    if (!map[qid].option_ids.includes(a.option_id)) {
                        map[qid].option_ids.push(a.option_id);
                    }
                }
                if (a.values && Array.isArray(a.values)) {
                    map[qid].values = [...(map[qid].values || []), ...a.values];
                }
            }
            if (a.option_id != null && (!map[qid].option_ids || map[qid].option_ids.length === 0)) {
                map[qid].option_ids = [a.option_id];
            }
        });
        return map;
    };

    answersMapToArray = (answersMap, questions) => {
        const result = [];
        Object.keys(answersMap).forEach(qid => {
            const a = answersMap[qid];
            if (!a) return;
            const question = (questions || []).find(q => String(q.id) === String(qid));
            const title = (question && question.title) || a.question_title || '';
            result.push({
                question_id: Number(qid),
                question_title: title,
                option_id: a.option_id ?? null,
                option_ids: a.option_ids && a.option_ids.length ? a.option_ids : undefined,
                value: a.value ?? null,
                value_num: a.value_num ?? null,
                values: a.values && a.values.length ? a.values : undefined,
            });
        });
        return result;
    };

    getAnswer = (questionId) => {
        return this.state.answersMap[questionId] || {
            question_id: questionId,
            option_id: null,
            option_ids: [],
            value: '',
            value_num: null,
            values: [],
        };
    };

    setAnswer = (questionId, patch) => {
        const answersMap = {...this.state.answersMap};
        const current = this.getAnswer(questionId);
        answersMap[questionId] = {...current, ...patch, question_id: questionId};
        this.setState({answersMap}, () => this.persistQuestionnaire());
    };

    persistQuestionnaire = () => {
        const {current_document} = this.props;
        if (!current_document) return;

        current_document.questionnaire = {
            answers: this.answersMapToArray(this.state.answersMap, this.state.questions),
            sellers: this.normalizeSellers(this.state.sellers),
        };

        window.postMessage({type: 'setCurrentSalePointMonitoring', value: current_document});
        this.autoSaveDocument(current_document);
    };

    autoSaveDocument = (document) => {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        this.saveTimeout = setTimeout(async () => {
            if (document && document.doc_data) {
                const api = new SalePointMonitoringDocsSync();
                const old_guid = document.doc_data.guid;
                const guid = await api.add(document);
                if (old_guid != guid) {
                    document.doc_data.guid = guid;
                    window.postMessage({type: 'setCurrentSalePointMonitoring', value: document});
                }
            }
        }, 2000);
    };

    componentDidMount() {
        super.componentDidMount();
        // If schema was empty at construct (cache miss), try again
        if (!this.state.questions.length) {
            const schema = getMonitoringQuestions();
            if (schema.questions && schema.questions.length) {
                this.setState({
                    questions: schema.questions,
                    tree: schema.tree || [],
                    qualifications: schema.qualifications || {},
                });
            }
        }
    }

    componentWillUnmount() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        Object.values(this.phoneMasks).forEach(m => {
            try { m.destroy(); } catch (e) { /* ignore */ }
        });
        super.componentWillUnmount && super.componentWillUnmount();
    }

    bindPhoneMask = (el, key, onAccept) => {
        if (!el) return;
        if (this.phoneMasks[key]) {
            return;
        }
        this.phoneMasks[key] = IMask(el, {
            mask: '+{7}(000)000-00-00',
        });
        this.phoneMasks[key].on('accept', () => {
            onAccept(this.phoneMasks[key].value);
        });
    };

    // --- Sellers ---
    addSeller = () => {
        const sellers = [...this.state.sellers, {
            guid: getGUID(),
            last_name: '',
            first_name: '',
            middle_name: '',
            birth_date: null,
            phone: '',
            position_id: 0,
            is_owner: 0,
            is_lpr: 0,
            qualification: 0,
            comment: '',
        }];
        this.setState({sellers}, () => this.persistQuestionnaire());
    };

    removeSeller = (index) => {
        const sellers = [...this.state.sellers];
        sellers[index] = {...sellers[index], removed: 1};
        // Keep removed for sync, but hide from UI by filtering in render
        this.setState({sellers}, () => this.persistQuestionnaire());
    };

    setSellerField = (index, field, value) => {
        const sellers = [...this.state.sellers];
        const next = {...sellers[index], [field]: value};
        if (field === 'position_id') {
            const positions = this.getSellerPositions();
            next.position_id = Number(value) || 0;
            next.position_title = positions[next.position_id] || positions[String(next.position_id)] || '';
        }
        sellers[index] = next;
        this.setState({sellers}, () => this.persistQuestionnaire());
    };

    normalizeSellers = (sellers) => {
        const positions = this.getSellerPositions();
        return (sellers || []).map(s => {
            const positionId = Number(s.position_id) || 0;
            return {
                ...s,
                position_id: positionId,
                position_title: s.position_title || positions[positionId] || positions[String(positionId)] || '',
            };
        });
    };

    // --- Tree helpers ---
    isTreeChildChecked = (questionId, groupId, typeId) => {
        const answer = this.getAnswer(questionId);
        const values = answer.values || [];
        return values.some(v => String(v.group_id) === String(groupId) && String(v.type_id) === String(typeId));
    };

    isTreeGroupChecked = (questionId, group) => {
        if (!group.children || !group.children.length) return false;
        return group.children.every(c => this.isTreeChildChecked(questionId, group.id, c.id));
    };

    toggleTreeChild = (questionId, group, child) => {
        const answer = this.getAnswer(questionId);
        let values = [...(answer.values || [])];
        const exists = values.findIndex(v => String(v.group_id) === String(group.id) && String(v.type_id) === String(child.id));
        if (exists >= 0) {
            values.splice(exists, 1);
        } else {
            values.push({
                group_id: group.id,
                type_id: child.id,
                label: `${group.title} / ${child.title}`,
                title: child.title,
            });
        }
        this.setAnswer(questionId, {values, value: JSON.stringify(values)});
    };

    toggleTreeGroup = (questionId, group) => {
        const answer = this.getAnswer(questionId);
        let values = [...(answer.values || [])];
        const allChecked = this.isTreeGroupChecked(questionId, group);
        // Remove all of this group
        values = values.filter(v => String(v.group_id) !== String(group.id));
        if (!allChecked) {
            (group.children || []).forEach(child => {
                values.push({
                    group_id: group.id,
                    type_id: child.id,
                    label: `${group.title} / ${child.title}`,
                    title: child.title,
                });
            });
        }
        this.setAnswer(questionId, {values, value: JSON.stringify(values)});
    };

    // --- Validation ---
    nextStep = () => {
        const {questions, answersMap, sellers} = this.state;
        const errors = {};
        let errorCount = 0;

        questions.forEach(q => {
            if (!q.required) return;
            if (q.type === 'sellers') {
                const active = sellers.filter(s => !s.removed);
                if (active.length === 0) {
                    errors[`q_${q.id}`] = 'Добавьте хотя бы одного сотрудника';
                    errorCount++;
                }
                return;
            }
            const a = answersMap[q.id];
            let empty = !a;
            if (a) {
                if (q.type === 'yesno') {
                    empty = a.value !== '1' && a.value !== '0' && a.value !== 1 && a.value !== 0;
                } else if (q.type === 'number') {
                    empty = a.value_num === null || a.value_num === '' || a.value_num === undefined;
                } else if (q.type === 'select') {
                    empty = a.option_id == null || a.option_id === '';
                } else if (q.type === 'multiselect') {
                    empty = !a.option_ids || a.option_ids.length === 0;
                } else if (q.type === 'tree') {
                    empty = !a.values || a.values.length === 0;
                } else {
                    empty = a.value === null || a.value === undefined || String(a.value).trim() === '';
                }
            }
            if (empty) {
                errors[`q_${q.id}`] = `Необходимо заполнить: ${q.title}`;
                errorCount++;
            }
        });

        // Required seller fields
        sellers.filter(s => !s.removed).forEach((s, idx) => {
            if (!s.last_name || !String(s.last_name).trim()) {
                errors[`seller_${idx}_last_name`] = 'Фамилия обязательна';
                errorCount++;
            }
            if (!s.first_name || !String(s.first_name).trim()) {
                errors[`seller_${idx}_first_name`] = 'Имя обязательно';
                errorCount++;
            }
        });

        this.setState({errors});
        if (errorCount > 0) {
            const first = Object.values(errors)[0];
            window.postMessage({type: 'error', text: first});
            return;
        }

        this.persistQuestionnaire();
        setQuizStep(6, 'SalePointMonitoring');
    };

    renderQuestion = (question) => {
        const answer = this.getAnswer(question.id);
        const error = this.state.errors[`q_${question.id}`] || '';
        const placeholder = (question.settings && question.settings.placeholder) || '';

        switch (question.type) {
            case 'yesno':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <SimpleCell Component="label" multiline
                                    after={<Switch checked={String(answer.value) === '1'}
                                                   onChange={e => this.setAnswer(question.id, {
                                                       value: e.currentTarget.checked ? '1' : '0'
                                                   })}/>}>
                            {String(answer.value) === '1' ? 'Да' : (String(answer.value) === '0' ? 'Нет' : '—')}
                        </SimpleCell>
                    </FormItem>
                );

            case 'number':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <KBInput value={answer.value_num ?? ''}
                                 onChange={(val) => this.setAnswer(question.id, {
                                     value_num: val === '' ? null : val,
                                     value: String(val)
                                 })}/>
                    </FormItem>
                );

            case 'text':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <Input value={answer.value || ''}
                               placeholder={placeholder}
                               onChange={e => this.setAnswer(question.id, {value: e.currentTarget.value})}/>
                    </FormItem>
                );

            case 'phone':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <Input getRef={el => this.bindPhoneMask(el, `q_${question.id}`, (val) => {
                            this.setAnswer(question.id, {value: val});
                        })}
                               defaultValue={answer.value || ''}
                               placeholder="+7(___)___-__-__"
                               type="tel"/>
                    </FormItem>
                );

            case 'textarea':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <Textarea value={answer.value || ''}
                                  rows={4}
                                  placeholder={placeholder}
                                  onChange={e => this.setAnswer(question.id, {value: e.currentTarget.value})}/>
                    </FormItem>
                );

            case 'date': {
                let dateVal = null;
                if (answer.value) {
                    const ts = Number(answer.value);
                    if (!isNaN(ts) && ts > 0) {
                        dateVal = new Date(ts * 1000);
                    }
                }
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <DateInput value={dateVal}
                                   onChange={v => {
                                       const ts = v ? Math.floor(v.getTime() / 1000) : null;
                                       this.setAnswer(question.id, {value: ts ? String(ts) : ''});
                                   }}/>
                    </FormItem>
                );
            }

            case 'select':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <RadioGroup>
                            {(question.options || []).map(opt => (
                                <Radio key={opt.id}
                                       name={`q_${question.id}`}
                                       value={opt.id}
                                       checked={String(answer.option_id) === String(opt.id)}
                                       onChange={() => this.setAnswer(question.id, {
                                           option_id: opt.id,
                                           value: opt.title
                                       })}>
                                    {opt.title}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </FormItem>
                );

            case 'multiselect':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        {(question.options || []).map(opt => {
                            const checked = (answer.option_ids || []).map(String).includes(String(opt.id));
                            return (
                                <SimpleCell key={opt.id} Component="label" multiline
                                            after={<Switch checked={checked}
                                                           onChange={() => {
                                                               let ids = [...(answer.option_ids || [])];
                                                               if (checked) {
                                                                   ids = ids.filter(id => String(id) !== String(opt.id));
                                                               } else {
                                                                   ids.push(opt.id);
                                                               }
                                                               this.setAnswer(question.id, {option_ids: ids});
                                                           }}/>}>
                                    {opt.title}
                                </SimpleCell>
                            );
                        })}
                    </FormItem>
                );

            case 'tree':
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        {(this.state.tree || []).map(group => (
                            <div key={group.id} style={{marginBottom: 12}}>
                                <SimpleCell Component="label" multiline
                                            after={<Switch checked={this.isTreeGroupChecked(question.id, group)}
                                                           onChange={() => this.toggleTreeGroup(question.id, group)}/>}>
                                    <b>{group.title}</b>
                                </SimpleCell>
                                {(group.children || []).map(child => (
                                    <SimpleCell key={child.id} Component="label" multiline
                                                style={{paddingLeft: 24}}
                                                after={<Switch
                                                    checked={this.isTreeChildChecked(question.id, group.id, child.id)}
                                                    onChange={() => this.toggleTreeChild(question.id, group, child)}/>}>
                                        {child.title}
                                    </SimpleCell>
                                ))}
                            </div>
                        ))}
                    </FormItem>
                );

            case 'sellers':
                return this.renderSellersBlock(question, error);

            default:
                return (
                    <FormItem key={question.id} top={question.title} bottom={error || question.hint || ''}
                              status={error ? 'error' : 'default'}>
                        <Input value={answer.value || ''}
                               onChange={e => this.setAnswer(question.id, {value: e.currentTarget.value})}/>
                    </FormItem>
                );
        }
    };

    getSellerPositions = () => {
        const positions = getTtStaffPositions();
        (this.state.sellers || []).forEach(s => {
            if (s && s.position_id && !positions[s.position_id]) {
                const all = getStaffPositions();
                if (all[s.position_id]) {
                    positions[s.position_id] = all[s.position_id];
                }
            }
        });
        return positions;
    };

    sellersQuestionText = (text, fallback) => {
        if (!text) {
            return fallback;
        }
        return String(text)
            .replace(/продавцах/g, 'сотрудниках')
            .replace(/Продавцах/g, 'Сотрудниках')
            .replace(/Продавцы/g, 'Сотрудники')
            .replace(/продавцы/g, 'сотрудники')
            .replace(/Продавец/g, 'Сотрудник')
            .replace(/продавец/g, 'сотрудник');
    };

    renderSellersBlock = (question, error) => {
        const activeSellers = this.state.sellers
            .map((s, index) => ({...s, _index: index}))
            .filter(s => !s.removed);
        const qualifications = this.state.qualifications || {};
        const positions = this.getSellerPositions();
        const blockTitle = this.sellersQuestionText(question.title, 'Сотрудники');
        const blockHint = this.sellersQuestionText(question.hint, '');

        return (
            <div key={question.id} style={{marginBottom: 16}}>
                <Div>
                    <Title level="3" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span>{blockTitle}</span>
                        <Button mode="secondary" onClick={this.addSeller} style={{width: 100}}>
                            <Icon20ListPlusOutline height={20}/>
                        </Button>
                    </Title>
                    {error && <Div style={{color: 'var(--vkui--color_text_negative)'}}>{error}</Div>}
                    {blockHint && <Div style={{opacity: 0.7, fontSize: 13}}>{blockHint}</Div>}
                </Div>

                {activeSellers.map((seller, displayIdx) => {
                    const index = seller._index;
                    const lnError = this.state.errors[`seller_${index}_last_name`];
                    const fnError = this.state.errors[`seller_${index}_first_name`];
                    let birthDate = null;
                    if (seller.birth_date) {
                        const ts = Number(seller.birth_date);
                        if (!isNaN(ts) && ts > 0) birthDate = new Date(ts * 1000);
                    }
                    return (
                        <Div key={seller.guid || index}
                             style={{border: '1px solid #e1e3e6', borderRadius: 8, marginBottom: 12, padding: 8}}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <b>Сотрудник {displayIdx + 1}</b>
                                <IconButton onClick={() => this.removeSeller(index)}>
                                    <Icon16Clear/>
                                </IconButton>
                            </div>
                            <FormLayout>
                                <FormItem top="Фамилия *" bottom={lnError || ''} status={lnError ? 'error' : 'default'}>
                                    <Input value={seller.last_name || ''}
                                           onChange={e => this.setSellerField(index, 'last_name', e.currentTarget.value)}/>
                                </FormItem>
                                <FormItem top="Имя *" bottom={fnError || ''} status={fnError ? 'error' : 'default'}>
                                    <Input value={seller.first_name || ''}
                                           onChange={e => this.setSellerField(index, 'first_name', e.currentTarget.value)}/>
                                </FormItem>
                                <FormItem top="Отчество">
                                    <Input value={seller.middle_name || ''}
                                           onChange={e => this.setSellerField(index, 'middle_name', e.currentTarget.value)}/>
                                </FormItem>
                                <FormItem top="Дата рождения">
                                    <DateInput value={birthDate}
                                               onChange={v => {
                                                   const ts = v ? Math.floor(v.getTime() / 1000) : null;
                                                   this.setSellerField(index, 'birth_date', ts);
                                               }}/>
                                </FormItem>
                                <FormItem top="Контактный тел">
                                    <Input getRef={el => this.bindPhoneMask(el, `seller_${seller.guid}`, (val) => {
                                        this.setSellerField(index, 'phone', val);
                                    })}
                                           defaultValue={seller.phone || ''}
                                           placeholder="+7(___)___-__-__"
                                           type="tel"/>
                                </FormItem>
                                <FormItem top="Должность">
                                    <NativeSelect value={String(seller.position_id || 0)}
                                                  onChange={e => this.setSellerField(
                                                      index,
                                                      'position_id',
                                                      Number((e.target && e.target.value) || e.currentTarget.value) || 0
                                                  )}>
                                        {Object.keys(positions).map(k => (
                                            <option key={`pos-${k}`} value={String(k)}>{positions[k]}</option>
                                        ))}
                                    </NativeSelect>
                                </FormItem>
                                <FormItem>
                                    <SimpleCell Component="label" multiline
                                                after={<Switch checked={!!seller.is_owner}
                                                               onChange={e => this.setSellerField(index, 'is_owner', e.currentTarget.checked ? 1 : 0)}/>}>
                                        Собственник
                                    </SimpleCell>
                                </FormItem>
                                <FormItem>
                                    <SimpleCell Component="label" multiline
                                                after={<Switch checked={!!seller.is_lpr}
                                                               onChange={e => this.setSellerField(index, 'is_lpr', e.currentTarget.checked ? 1 : 0)}/>}>
                                        ЛПР
                                    </SimpleCell>
                                </FormItem>
                                <FormItem top="Квалификация сотрудника">
                                    <NativeSelect value={seller.qualification || ''}
                                                  onChange={e => this.setSellerField(index, 'qualification', Number(e.currentTarget.value) || 0)}>
                                        <option value="">Не указана</option>
                                        {Object.keys(qualifications).map(k => (
                                            <option key={k} value={k}>{qualifications[k]}</option>
                                        ))}
                                    </NativeSelect>
                                </FormItem>
                                <FormItem top="Комментарий">
                                    <Textarea value={seller.comment || ''}
                                              rows={2}
                                              onChange={e => this.setSellerField(index, 'comment', e.currentTarget.value)}/>
                                </FormItem>
                            </FormLayout>
                        </Div>
                    );
                })}

                {activeSellers.length === 0 && (
                    <Div style={{opacity: 0.6}}>Нет сотрудников. Нажмите «+», чтобы добавить.</Div>
                )}
            </div>
        );
    };

    render() {
        const {questions} = this.state;

        return (
            <div>
                <SplitLayout>
                    <SplitCol>
                        <PanelHeader fixed={true}>
                            <Div>
                                Мониторинг точки. Шаг 5. Опросник
                                {this.props.isOffline && <Chip removable={false} className="all-docs"
                                                               id="is_offline">Offline режим<HelpButton
                                    text={"Нет связи с интернет. Работаем в автономном режиме"}/></Chip>}
                            </Div>
                        </PanelHeader>
                    </SplitCol>
                </SplitLayout>

                <FormLayout style={{paddingBottom: 140}}>
                    {questions.length === 0 && (
                        <Div style={{opacity: 0.7, marginBottom: 16}}>
                            Нет активных вопросов опросника. Можно перейти дальше.
                        </Div>
                    )}
                    {questions.map(q => this.renderQuestion(q))}
                </FormLayout>

                <SplitLayout>
                    <SplitCol>
                        <Div className="action-board">
                            <ButtonGroup mode="horizontal" stretched>
                                <Button stretched size={AdaptivityContext.isMobile ? 'm' : 'l'}
                                        onClick={() => setQuizStep(4, 'SalePointMonitoring')}>Назад</Button>
                                <Button stretched size={AdaptivityContext.isMobile ? 'm' : 'l'}
                                        onClick={this.nextStep}>Далее</Button>
                            </ButtonGroup>
                        </Div>
                    </SplitCol>
                </SplitLayout>
            </div>
        );
    }
}

export default withOffline(SalePointMonitoringStep4Questionnaire);
